/**
 * Mensagens de erro de autenticação compartilhadas.
 *
 * Fonte única de verdade para o texto exibido quando o login falha, reusada
 * tanto pelo toast (auth.context) quanto pelo texto inline abaixo do campo de
 * senha (LoginPage), garantindo que as duas exibições nunca divirjam.
 */

interface LoginErrorMessage {
  title: string;
  description: string;
}

type AxiosLikeError = {
  response?: {
    status?: number;
    data?: {
      detail?: unknown;
    };
  };
};

/**
 * Deriva a mensagem exibida quando o login falha.
 *
 * Credenciais inválidas (401) recebem uma mensagem genérica de propósito
 * (anti-enumeração de contas): não revela se o e-mail existe e cobre todos os
 * cenários possíveis — e-mail/senha incorretos, conta inexistente ou excluída
 * por inatividade.
 */
export const getLoginErrorMessage = (error: unknown): LoginErrorMessage => {
  const axiosError = error as AxiosLikeError | null | undefined;
  const isInvalidCredentials = axiosError?.response?.status === 401;

  if (isInvalidCredentials) {
    return {
      title: "Não foi possível entrar",
      description:
        "Verifique seu e-mail e senha. A conta também pode não estar cadastrada ou ter sido excluída após 60 dias de inatividade.",
    };
  }

  const detail = axiosError?.response?.data?.detail;
  return {
    title: "Erro ao fazer login",
    description:
      typeof detail === "string" && detail.trim().length > 0
        ? detail
        : "Verifique suas credenciais.",
  };
};

/**
 * A credencial já foi criada no backend (ex.: senha definida pelo convite), mas
 * a sessão não pôde ser aberta no app. A tela deve orientar o login — repetir a
 * ação original falharia, porque o link/token já foi consumido.
 */
export class SessionStartError extends Error {
  readonly cause: unknown;

  constructor(cause: unknown) {
    super('Não foi possível abrir a sessão');
    this.name = 'SessionStartError';
    this.cause = cause;
  }
}
